import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { MOCK_PAYMENTS } from '../lib/mockData';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { CreditCard, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
export function CommissionPayments() {
  const currentStatement = {
    period: 'Jan 29 - Feb 4, 2024',
    sales: 6333.33,
    rate: 0.15,
    amount: 950.0,
    status: 'pending'
  };
  return <div className="space-y-8" data-id="element-290">
      <div data-id="element-291">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-292">
          Commission & Payments
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-293">
          Manage your commission payments and view billing history.
        </p>
      </div>

      {/* Current Statement Card */}
      <div className="grid gap-6 md:grid-cols-2" data-id="element-294">
        <Card className="bg-slate-900 text-white border-slate-800" data-id="element-295">
          <CardHeader data-id="element-296">
            <CardTitle className="text-lg font-medium text-slate-200" data-id="element-297">
              Current Statement
            </CardTitle>
            <CardDescription className="text-slate-400" data-id="element-298">
              Period: {currentStatement.period}
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-299">
            <div className="flex justify-between items-end mb-6" data-id="element-300">
              <div data-id="element-301">
                <p className="text-sm text-slate-400 mb-1" data-id="element-302">Net Amount Due</p>
                <div className="text-4xl font-bold text-white" data-id="element-303">
                  {formatCurrency(currentStatement.amount)}
                </div>
              </div>
              <Badge variant="info" className="bg-blue-500/20 text-blue-200 border-blue-500/30" data-id="element-304">
                Upcoming Charge
              </Badge>
            </div>
            <div className="space-y-3 pt-4 border-t border-slate-800 text-sm" data-id="element-305">
              <div className="flex justify-between text-slate-300" data-id="element-306">
                <span data-id="element-307">Total Tracked Sales</span>
                <span data-id="element-308">{formatCurrency(currentStatement.sales)}</span>
              </div>
              <div className="flex justify-between text-slate-300" data-id="element-309">
                <span data-id="element-310">Commission Rate</span>
                <span data-id="element-311">15%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-312">
          <CardHeader data-id="element-313">
            <CardTitle data-id="element-314">Payment Method</CardTitle>
            <CardDescription data-id="element-315">
              Commission charges are deducted from this account.
            </CardDescription>
          </CardHeader>
          <CardContent data-id="element-316">
            <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg bg-slate-50 mb-4" data-id="element-317">
              <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center border border-slate-200 shadow-sm" data-id="element-318">
                <CreditCard className="h-5 w-5 text-slate-600" data-id="element-319" />
              </div>
              <div data-id="element-320">
                <p className="font-medium text-slate-900" data-id="element-321">
                  Stripe Connect Account
                </p>
                <p className="text-sm text-slate-500" data-id="element-322">**** 4242 • Connected</p>
              </div>
              <Badge variant="success" className="ml-auto" data-id="element-323">
                Active
              </Badge>
            </div>
            <Button variant="outline" className="w-full" data-id="element-324">
              Update Payment Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Payment History */}
      <Card data-id="element-325">
        <CardHeader data-id="element-326">
          <CardTitle data-id="element-327">Payment History</CardTitle>
        </CardHeader>
        <CardContent data-id="element-328">
          <div className="overflow-x-auto" data-id="element-329">
            <table className="w-full text-sm text-left" data-id="element-330">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-331">
                <tr data-id="element-332">
                  <th className="px-4 py-3" data-id="element-333">Payment ID</th>
                  <th className="px-4 py-3" data-id="element-334">Period</th>
                  <th className="px-4 py-3" data-id="element-335">Amount</th>
                  <th className="px-4 py-3" data-id="element-336">Status</th>
                  <th className="px-4 py-3" data-id="element-337">Attempts</th>
                  <th className="px-4 py-3 text-right" data-id="element-338">Action</th>
                </tr>
              </thead>
              <tbody data-id="element-339">
                {MOCK_PAYMENTS.map(payment => <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50" data-id="element-340">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500" data-id="element-341">
                      {payment.id}
                    </td>
                    <td className="px-4 py-3 text-slate-900" data-id="element-342">
                      {formatDate(payment.periodStart)} -{' '}
                      {formatDate(payment.periodEnd)}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-343">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-4 py-3" data-id="element-344">
                      <div className="flex items-center gap-2" data-id="element-345">
                        {payment.status === 'paid' && <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-346" />}
                        {payment.status === 'failed' && <AlertCircle className="h-4 w-4 text-red-500" data-id="element-347" />}
                        {payment.status === 'processing' && <Clock className="h-4 w-4 text-amber-500" data-id="element-348" />}
                        {payment.status === 'pending' && <Clock className="h-4 w-4 text-blue-500" data-id="element-349" />}
                        <Badge variant={payment.status === 'paid' ? 'success' : payment.status === 'failed' ? 'destructive' : payment.status === 'processing' ? 'warning' : 'info'} data-id="element-350">
                          {payment.status}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500" data-id="element-351">
                      {payment.attemptCount}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-352">
                      {payment.status === 'failed' ? <Button variant="destructive" size="sm" className="h-7 text-xs" data-id="element-353">
                          Retry Payment
                        </Button> : <Button variant="ghost" size="sm" className="h-7 text-xs" data-id="element-354">
                          View Invoice
                        </Button>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>;
}